import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormOptionsSelector from "@babackapp/react-hook-forms-components/form-option-selector";
import useTranslation from "@babackapp/translations/use-translation";
import { FormField } from "@babackapp/ui/components/form";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ReturnReasonFormValues } from "./add-return-reason-dialog";

export default function ReturnReasonOptionsForm() {
  const t = useTranslation();
  const { control, setValue } = useFormContext<ReturnReasonFormValues>();
  const babackReasonOptions = useStandardReasonOptions();
  const additionalOptions = useAdditionalInfoOptions();

  const babackStandardReason = useWatch({
    control,
    name: "babackStandardReason",
  });

  /**
   * When the reason is OTHER, we need a note,
   * when it is not, we can remove that note
   * if it exists
   */
  useEffect(() => {
    if (babackStandardReason !== "OTHER") {
      setValue("babackStandardReasonNote", "");
    }
  }, [babackStandardReason, setValue]);

  return (
    <div className="pt-8 flex flex-col gap-8">
      <FormField
        control={control}
        name="babackStandardReason"
        render={({ field: { value, onChange } }) => (
          <FormOptionsSelector
            label={t("settings.reasons.fields.shopifyEquivalence.title")}
            description={t(
              "settings.reasons.fields.shopifyEquivalence.description",
            )}
            options={babackReasonOptions}
            value={value}
            onChange={onChange}
          />
        )}
      />

      {babackStandardReason === "OTHER" && (
        <FormField
          control={control}
          name="babackStandardReasonNote"
          rules={{
            validate: (data) => {
              if (babackStandardReason === "OTHER" && !data) {
                return false;
              }
              return true;
            },
          }}
          render={({ field }) => (
            <FormInput
              label={t("settings.reasons.fields.reasonNote.title")}
              description={t("settings.reasons.fields.reasonNote.description")}
              value={field.value ?? ""}
              onChange={field.onChange}
            />
          )}
        />
      )}

      <FormField
        control={control}
        name="images"
        render={({ field: { value, onChange } }) => (
          <FormOptionsSelector
            label={t("settings.reasons.fields.imageField.title")}
            description={t("settings.reasons.fields.imageField.description")}
            options={additionalOptions}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <FormField
        control={control}
        name="comments"
        render={({ field: { value, onChange } }) => (
          <FormOptionsSelector
            label={t("settings.reasons.fields.commentField.title")}
            description={t("settings.reasons.fields.commentField.description")}
            options={additionalOptions}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <FormField
        control={control}
        name="externalId"
        render={({ field }) => (
          <FormInput
            label={t("settings.reasons.fields.externalId.title")}
            description={t("settings.reasons.fields.externalId.description")}
            value={field.value ?? ""}
            onChange={field.onChange}
          />
        )}
      />
    </div>
  );
}

function useAdditionalInfoOptions() {
  const t = useTranslation();
  return [
    {
      key: "NOT_AVAILABLE",
      label: t("settings.reasons.fields.additionalOptions.notAvailable"),
    },
    {
      key: "OPTIONAL",
      label: t("settings.reasons.fields.additionalOptions.optional"),
    },
    {
      key: "REQUIRED",
      label: t("settings.reasons.fields.additionalOptions.required"),
    },
  ];
}

/**
 * Mapping between Baback standard reasons and their Shopify equivalence.
 * Today 1:1, but kept as a map to support future divergence
 * (new Baback-only reasons or new Shopify reasons).
 */
const SHOPIFY_EQUIVALENCE: Record<string, string> = {
  COLOR: "COLOR",
  DEFECTIVE: "DEFECTIVE",
  NOT_AS_DESCRIBED: "NOT_AS_DESCRIBED",
  OTHER: "OTHER",
  STYLE: "STYLE",
  SIZE_TOO_LARGE: "SIZE_TOO_LARGE",
  SIZE_TOO_SMALL: "SIZE_TOO_SMALL",
  UNKNOWN: "UNKNOWN",
  UNWANTED: "UNWANTED",
  WRONG_ITEM: "WRONG_ITEM",
};

function useStandardReasonOptions() {
  const t = useTranslation();
  return [
    {
      key: "COLOR",
      label: t("settings.reasons.babackStandardReasons.color"),
      description: t("settings.reasons.babackStandardReasonsDetails.color"),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.COLOR}`,
    },
    {
      key: "DEFECTIVE",
      label: t("settings.reasons.babackStandardReasons.defective"),
      description: t("settings.reasons.babackStandardReasonsDetails.defective"),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.DEFECTIVE}`,
    },
    {
      key: "NOT_AS_DESCRIBED",
      label: t("settings.reasons.babackStandardReasons.notAsDescribed"),
      description: t(
        "settings.reasons.babackStandardReasonsDetails.notAsDescribed",
      ),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.NOT_AS_DESCRIBED}`,
    },
    {
      key: "OTHER",
      label: t("settings.reasons.babackStandardReasons.other"),
      description: t("settings.reasons.babackStandardReasonsDetails.other"),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.OTHER}`,
    },
    {
      key: "STYLE",
      label: t("settings.reasons.babackStandardReasons.style"),
      description: t("settings.reasons.babackStandardReasonsDetails.style"),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.STYLE}`,
    },
    {
      key: "SIZE_TOO_LARGE",
      label: t("settings.reasons.babackStandardReasons.sizeTooLarge"),
      description: t(
        "settings.reasons.babackStandardReasonsDetails.sizeTooLarge",
      ),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.SIZE_TOO_LARGE}`,
    },
    {
      key: "SIZE_TOO_SMALL",
      label: t("settings.reasons.babackStandardReasons.sizeTooSmall"),
      description: t(
        "settings.reasons.babackStandardReasonsDetails.sizeTooSmall",
      ),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.SIZE_TOO_SMALL}`,
    },
    {
      key: "UNKNOWN",
      label: t("settings.reasons.babackStandardReasons.unknown"),
      description: t("settings.reasons.babackStandardReasonsDetails.unknown"),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.UNKNOWN}`,
    },
    {
      key: "UNWANTED",
      label: t("settings.reasons.babackStandardReasons.unwanted"),
      description: t("settings.reasons.babackStandardReasonsDetails.unwanted"),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.UNWANTED}`,
    },
    {
      key: "WRONG_ITEM",
      label: t("settings.reasons.babackStandardReasons.wrongItem"),
      description: t("settings.reasons.babackStandardReasonsDetails.wrongItem"),
      hint: `Shopify: ${SHOPIFY_EQUIVALENCE.WRONG_ITEM}`,
    },
  ];
}
